import { useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { usePreisToolSupplierOffers, useRemoveManualOffer } from '@/api/preis-tool/preis-tool.hooks';
import type {
  PreisToolSupplierOffer,
  SupplierOfferMatchKind,
  SupplierOfferSource,
} from '@/api/preis-tool/preis-tool.types';
import { DsAlert, DsButton, DsModal } from '@/modules/internal-v2/ds';
import { DsTable } from '@/modules/internal-v2/shared/ds-table.component';
import {
  fmtDate,
  fmtDateTime,
  fmtEUR,
  marginClass,
  UPSTREAM_SUPPLIERS_ENABLED,
  offerStockSummary,
  upstreamStockSummary,
} from '../helpers';
import { ManualOfferDialog } from './manual-offer-dialog.component';
import { ShippingCostDialog } from './shipping-cost-dialog.component';

interface SupplierOffersProps {
  itemId: string;
  itemName: string;
  mek: number;
  /** Whether the user may enter and delete offers (`PREIS_TOOL_MANAGE`). */
  canEdit: boolean;
  /** Fixed prices of the item that have run out; they are no offers any more. */
  expiredFixedPriceCount?: number;
  /** Opens the fixed price dialog; with an id unfolded at that fixed price's stock. */
  onOpenFixedPrices?: (fixedPriceId?: string) => void;
}

const col = createColumnHelper<PreisToolSupplierOffer>();

const SOURCE: Record<SupplierOfferSource, { label: string; cls: string; title: string }> = {
  feed: { label: 'Feed', cls: 'muted', title: 'Automatisch abgerufene Preisliste des Lieferanten' },
  fixed_price: { label: 'Fixpreis', cls: 'info', title: 'Mit dem Lieferanten vereinbarter fester Einkaufspreis' },
  manual: { label: 'manuell', cls: 'warn', title: 'Von Hand erfasstes Angebot (Telefon, E-Mail)' },
};

/** Tooltip of the warning sign on feed offers whose last run looked incomplete (EOS-184). */
const FEED_WARNING_TEXT =
  'Der letzte Abruf dieser Preisliste war möglicherweise unvollständig. Fehlende Angebote wurden nicht ausgeblendet; dieses Angebot ist eventuell nicht mehr aktuell.';

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

function matchLabel(k: SupplierOfferMatchKind) {
  return k === 'ean' ? 'EAN' : k === 'manual' ? 'manuell' : 'MPN';
}

function matchClass(k: SupplierOfferMatchKind) {
  return k === 'ean' ? 'pos' : k === 'manual' ? 'muted' : 'info';
}

/** Why an offer is (not) the one to go by; `null` for an ordinary valid offer. */
function offerStatus(offer: PreisToolSupplierOffer): { label: string; cls: string; title: string } | null {
  if (offer.isBest) {
    return { label: 'günstigstes', cls: 'pos', title: 'Günstigstes gültiges Angebot (Nettopreis in EUR)' };
  }
  switch (offer.validity) {
    case 'no_price':
      return { label: 'ohne Preis', cls: 'muted', title: 'Der Preis ist 0. Zählt nicht für den Vergleich.' };
    case 'expired':
      return { label: 'abgelaufen', cls: 'neg', title: 'Das Gültigkeitsdatum ist überschritten. Zählt nicht für den Vergleich.' };
    case 'not_yet_valid':
      return { label: 'noch nicht gültig', cls: 'warn', title: 'Gilt erst ab einem späteren Datum. Zählt nicht für den Vergleich.' };
    case 'not_comparable':
      return {
        label: 'Fremdwährung',
        cls: 'muted',
        title: `Preis in ${offer.currency}. Fremdwährungen werden nicht umgerechnet und zählen nicht für den Vergleich.`,
      };
    default:
      return null;
  }
}

/** An offer that takes no part in the best-offer comparison. */
const isSetAside = (offer: PreisToolSupplierOffer) => offer.validity !== 'valid';

export function SupplierOffers({
  itemId,
  itemName,
  mek,
  canEdit,
  expiredFixedPriceCount = 0,
  onOpenFixedPrices,
}: SupplierOffersProps) {
  const { data, isPending, isError } = usePreisToolSupplierOffers(itemId);
  const {
    mutate: deleteManualOffer,
    reset: resetDeleteManualOffer,
    isPending: isDeletingManualOffer,
    isError: deleteManualOfferFailed,
  } = useRemoveManualOffer();
  const [manualOfferOpen, setManualOfferOpen] = useState(false);
  // The manual offer whose deletion waits for confirmation.
  const [deleting, setDeleting] = useState<PreisToolSupplierOffer | null>(null);
  const [shippingFor, setShippingFor] = useState<{
    supplierBpId: number;
    supplierName: string;
    current: number | null;
  } | null>(null);
  // No initial sorting: the backend order (valid offers first, cheapest in EUR
  // on top) is the one to show. A third click on a header returns to it.
  const [sorting, setSorting] = useState<SortingState>([]);

  const rows = useMemo(() => data?.offers ?? [], [data]);

  const columns = useMemo(
    () => [
      col.display({
        id: 'status',
        header: 'Status',
        size: 152,
        enableSorting: false,
        cell: (info) => {
          const status = offerStatus(info.row.original);
          return status ? (
            <span className={`ds-pill ${status.cls}`} title={status.title}>
              {status.label}
            </span>
          ) : null;
        },
      }),
      col.accessor('matchKind', {
        id: 'matchKind',
        header: 'Match',
        size: 74,
        // Explicit: TanStack only guesses "text" from row 11 on, and offer
        // tables are shorter — it would then sort case-sensitively.
        sortingFn: 'text',
        cell: (info) => {
          const v = info.getValue();
          return <span className={`ds-pill ${matchClass(v)}`}>{matchLabel(v)}</span>;
        },
      }),
      col.accessor('description', {
        id: 'description',
        header: 'Bezeichnung',
        meta: { flex: true },
        sortingFn: 'text',
        cell: (info) => {
          const v = info.getValue();
          return (
            <span
              style={{
                fontWeight: 500,
                color: v ? 'var(--ink)' : 'var(--ink-4)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={v ?? undefined}
            >
              {v ?? '—'}
            </span>
          );
        },
      }),
      col.accessor('supplierName', {
        id: 'supplierName',
        header: 'Lieferant',
        size: 160,
        sortingFn: 'text',
        cell: (info) => (
          <span
            style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            title={
              info.row.original.feedName
                ? `${info.getValue()} · Preisliste: ${info.row.original.feedName}`
                : info.getValue()
            }
          >
            {info.getValue()}
          </span>
        ),
      }),
      col.accessor('source', {
        id: 'source',
        header: 'Quelle',
        size: 68,
        enableSorting: false,
        cell: (info) => (
          <span className={`ds-pill ${SOURCE[info.getValue()].cls}`} title={SOURCE[info.getValue()].title}>
            {SOURCE[info.getValue()].label}
          </span>
        ),
      }),
      col.accessor('supplierSku', {
        id: 'supplierSku',
        header: 'Lieferant-SKU',
        size: 120,
        sortingFn: 'alphanumeric',
        cell: (info) => (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-xs)',
              color: info.getValue() ? undefined : 'var(--ink-4)',
            }}
          >
            {info.getValue() ?? '—'}
          </span>
        ),
      }),
      col.accessor('manufacturerCode', {
        id: 'manufacturerCode',
        header: 'MPN',
        size: 116,
        sortingFn: 'alphanumeric',
        cell: (info) => {
          const v = info.getValue();
          return (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: v ? 'var(--ink-2)' : 'var(--ink-4)' }}>
              {v ?? '—'}
            </span>
          );
        },
      }),
      col.accessor('ean', {
        id: 'ean',
        header: 'GTIN/EAN',
        size: 116,
        sortingFn: 'alphanumeric',
        cell: (info) => {
          const v = info.getValue();
          return (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: v ? 'var(--ink-2)' : 'var(--ink-4)' }}>
              {v ?? '—'}
            </span>
          );
        },
      }),
      col.accessor('netPrice', {
        id: 'netPrice',
        header: 'Netto',
        size: 96,
        meta: { numeric: true },
        cell: (info) => {
          const row = info.row.original;
          return (
            <span
              style={{ fontFamily: 'var(--font-mono)' }}
              title={row.currency !== 'EUR' && row.netPriceEur != null ? `umgerechnet € ${fmtEUR(row.netPriceEur)}` : undefined}
            >
              {fmtEUR(row.netPrice)} <span style={{ color: 'var(--ink-3)' }}>{row.currency}</span>
            </span>
          );
        },
        sortingFn: (a, b) =>
          (a.original.netPriceEur ?? Number.POSITIVE_INFINITY) - (b.original.netPriceEur ?? Number.POSITIVE_INFINITY),
      }),
      col.accessor('shippingCost', {
        id: 'shippingCost',
        header: 'Versand',
        size: 64,
        meta: { numeric: true },
        enableSorting: false,
        cell: (info) => {
          const v = info.getValue();
          const row = info.row.original;
          const text = (
            <span
              style={{ fontFamily: 'var(--font-mono)', color: v == null ? 'var(--ink-4)' : 'var(--ink)' }}
              title={v == null ? 'Keine Versandkosten bekannt' : undefined}
            >
              {v == null ? '—' : fmtEUR(v)}
            </span>
          );
          // Shipping costs belong to a supplier business partner; free-text suppliers have none.
          if (!canEdit || row.supplierBpId == null) return text;
          return (
            <button
              className="pt-cell-button"
              title={`Versandkosten für ${row.supplierName} pflegen`}
              onClick={() =>
                setShippingFor({ supplierBpId: row.supplierBpId!, supplierName: row.supplierName, current: v })
              }
            >
              {text}
            </button>
          );
        },
      }),
      col.display({
        id: 'savings',
        header: 'Δ MEK',
        size: 76,
        meta: { numeric: true },
        cell: (info) => {
          const row = info.row.original;
          if (mek <= 0 || row.netPriceEur == null) return <span style={{ color: 'var(--ink-4)' }}>—</span>;
          const diffPct = (mek - row.netPriceEur) / mek;
          const cls = marginClass(diffPct);
          return (
            <span className={`ds-pill ${cls}`}>
              {diffPct >= 0 ? '+' : ''}
              {(diffPct * 100).toFixed(1)}%
            </span>
          );
        },
        sortingFn: (a, b) =>
          (a.original.netPriceEur ?? Number.POSITIVE_INFINITY) - (b.original.netPriceEur ?? Number.POSITIVE_INFINITY),
      }),
      col.accessor('validTo', {
        id: 'validTo',
        header: 'Gültig bis',
        size: 82,
        cell: (info) => {
          const v = info.getValue();
          return (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: v ? 'var(--ink-2)' : 'var(--ink-4)' }}>
              {v ? fmtDay(v) : '—'}
            </span>
          );
        },
      }),
      col.accessor('minQty', {
        id: 'minQty',
        header: 'MOQ',
        size: 50,
        meta: { numeric: true },
        cell: (info) => (
          <span style={{ fontFamily: 'var(--font-mono)' }}>{info.getValue()}</span>
        ),
      }),
      col.accessor('leadTimeDays', {
        id: 'leadTimeDays',
        header: 'Lead',
        size: 56,
        meta: { numeric: true },
        cell: (info) => {
          const v = info.getValue();
          return (
            <span style={{ fontFamily: 'var(--font-mono)', color: v == null ? 'var(--ink-4)' : 'var(--ink)' }}>
              {v == null ? '—' : `${v}d`}
            </span>
          );
        },
      }),
      col.accessor('stockQty', {
        id: 'stockQty',
        header: 'Lager',
        size: 128,
        meta: { numeric: true },
        cell: (info) => {
          const v = info.getValue();
          const offer = info.row.original;
          const upstream = offer.upstreamSuppliers;
          if (offer.source === 'fixed_price' || offer.source === 'manual') {
            // What the supplier has itself plus what its upstream suppliers hold;
            // the tooltip splits it up.
            const stock = offerStockSummary(offer.offeredQty, upstream);
            const value =
              stock.total != null ? (
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>
                  {stock.total.toLocaleString('de-DE')}
                </span>
              ) : !upstream || !UPSTREAM_SUPPLIERS_ENABLED ? (
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-4)' }}>—</span>
              ) : (
                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                  {upstreamStockSummary(upstream).text}
                </span>
              );
            // A fixed price's stock is kept in its dialog: a click opens it there.
            if (offer.source !== 'fixed_price' || !onOpenFixedPrices) {
              return <span title={stock.title}>{value}</span>;
            }
            return (
              <button
                className="pt-cell-button"
                title={`${stock.title}\nKlick: Lager im Fixpreis-Dialog öffnen`}
                onClick={() => onOpenFixedPrices(offer.id)}
              >
                {value}
              </button>
            );
          }
          // A feed offer: its stock, the next delivery the feed announces, and when it last reported.
          const feedTitle = [
            `Bestand laut Feed: ${v == null ? 'unbekannt' : `${v.toLocaleString('de-DE')} Stk`}`,
            offer.stockNextDeliveryAt &&
              `Nächste Lieferung: ${fmtDate(offer.stockNextDeliveryAt)}${
                offer.stockNextDeliveryQty ? `, ${offer.stockNextDeliveryQty.toLocaleString('de-DE')} Stk` : ''
              }`,
            offer.stockLastSeenAt && `Stand: ${fmtDateTime(offer.stockLastSeenAt)}`,
          ]
            .filter(Boolean)
            .join('\n');
          return (
            <span
              style={{ fontFamily: 'var(--font-mono)', color: v == null ? 'var(--ink-4)' : 'var(--ink)' }}
              title={feedTitle}
            >
              {v == null ? '—' : v}
            </span>
          );
        },
      }),
      col.accessor('lastSeenAt', {
        id: 'lastSeenAt',
        header: 'Stand',
        // Room for the warning sign of an incomplete last run.
        size: 96,
        cell: (info) => {
          const v = info.getValue();
          const date = (
            <span
              style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: v ? 'var(--ink-2)' : 'var(--ink-4)' }}
              title={v ? `Zuletzt vom Abruf der Preisliste bestätigt: ${fmtDateTime(v)}` : undefined}
            >
              {v ? fmtDay(v) : '—'}
            </span>
          );
          if (info.row.original.feedWarning !== 'last_run_incomplete') return date;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {date}
              <span className="ds-flag warn" title={FEED_WARNING_TEXT} aria-label={FEED_WARNING_TEXT}>
                !
              </span>
            </span>
          );
        },
      }),
      col.display({
        id: 'actions',
        header: '',
        size: 70,
        enableSorting: false,
        cell: (info) =>
          canEdit && info.row.original.source === 'manual' ? (
            <button
              className="ds-btn ghost small"
              onClick={() => {
                resetDeleteManualOffer();
                setDeleting(info.row.original);
              }}
              title="Manuell erfasstes Angebot löschen"
            >
              Löschen
            </button>
          ) : null,
      }),
    ],
    [mek, canEdit, resetDeleteManualOffer, onOpenFixedPrices],
  );

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (r) => r.id,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="pt-supplier-offers">
      <div className="pt-supplier-offers-header">
        {rows.length > 0 && data?.bestOfferId == null && (
          <span className="ds-flag warn" title="Alle gefundenen Angebote sind abgelaufen, noch nicht gültig, ohne Preis oder in Fremdwährung.">
            Kein gültiges Angebot
          </span>
        )}
        {expiredFixedPriceCount > 0 && onOpenFixedPrices && (
          <button className="ds-btn ghost small" onClick={() => onOpenFixedPrices()}>
            {expiredFixedPriceCount} abgelaufene{expiredFixedPriceCount === 1 ? 'r' : ''} Fixpreis{expiredFixedPriceCount === 1 ? '' : 'e'} ansehen
          </button>
        )}
        {canEdit && (
          <button className="ds-btn small" onClick={() => setManualOfferOpen(true)}>
            Angebot erfassen
          </button>
        )}
        <div className="hint">
          Heuristisches Matching · EAN ({data?.itemBarCode ?? '—'}) oder Herstellercode ({itemId})
        </div>
      </div>
      <DsTable
        table={table}
        fixedLayout
        className="pt-offers-table"
        rowClassName={(offer) => (isSetAside(offer) ? 'pt-offer-set-aside' : '')}
        emptyMessage={
          isPending
            ? 'Lade…'
            : isError
              ? 'Fehler beim Laden der Angebote.'
              : 'Keine passenden Lieferanten-Angebote gefunden.'
        }
      />
      {manualOfferOpen && (
        <ManualOfferDialog itemId={itemId} itemName={itemName} onClose={() => setManualOfferOpen(false)} />
      )}
      {shippingFor && (
        <ShippingCostDialog {...shippingFor} onClose={() => setShippingFor(null)} />
      )}
      {deleting && (
        <DsModal
          open
          onClose={() => setDeleting(null)}
          title="Angebot löschen"
          description={`${deleting.supplierName} · € ${fmtEUR(deleting.netPrice)}${
            deleting.validTo ? ` · gültig bis ${fmtDay(deleting.validTo)}` : ''
          }`}
          footer={
            <>
              <DsButton
                variant="ghost"
                onClick={() => setDeleting(null)}
                disabled={isDeletingManualOffer}
              >
                Abbrechen
              </DsButton>
              <DsButton
                variant="primary"
                onClick={() => deleteManualOffer(deleting.id, { onSuccess: () => setDeleting(null) })}
                disabled={isDeletingManualOffer}
              >
                {isDeletingManualOffer ? 'Löschen…' : 'Löschen'}
              </DsButton>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--ink-2)' }}>
            Das manuell erfasste Angebot wird dauerhaft entfernt und zählt nicht mehr für den Vergleich.
          </p>
          {deleteManualOfferFailed && (
            <DsAlert severity="error">Das Angebot konnte nicht gelöscht werden.</DsAlert>
          )}
        </DsModal>
      )}
    </div>
  );
}
